import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  useTheme,
  SelectChangeEvent,
} from '@mui/material'
import axios, { CancelTokenSource } from 'axios'
import useSessionApi from 'components/cell-visualizations/useSessionApi'
import { DeepcellDialog, DeepcellPrimarySelect, DeepcellTextField } from 'components/shared'
import useFlagCondition from 'components/shared/useFlagCondition'
import useFlags from 'components/shared/useFlags'
import _ from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from 'react-query'
import { useNotificationSlice } from 'redux/slices/hooks'
import useEventsManager from 'redux/slices/hooks/useEventsManager'
import { getSession, getSessions, Run, SamplingStrategy, VisualizationSessionConfig, getSamplingStrategyLabel, RunSamplingConfig, SessionMetaData, getCellRecordCounts } from 'utils/api'
import { v4 as uuidv4 } from 'uuid'
import { DeepcellFormControlLabel } from 'components/shared/DeepcellFormControlLabel'
import useCellVisualizationUrlParams from '../useCellVisualizationUrlParams'
import { FieldsToInclude } from './FieldsToInclude'
import {
  DEFAULT_SAMPLE_SIZE,
  estimateCreationTime,
  getAvailableSampleSizeOptions,
  getDefaultOption,
  totalCellCountInRuns,
} from './utils'
import { CustomSamplingConfig } from './CustomSamplingConfig'

interface StartAnalysisDialogProps {
  isOpen: boolean
  handleClose?(): void
  handleStartAnalysis?(): void
  runs: Run[]
  /**
   * When this is populated, pass this to the backend instead of "runs"
   */
  customRunData?: Record<string, unknown>[]
  hideMorphometricsModel?: boolean
  defaultSessionId?: number
}

// Sentinel value — when selected, projection_model_id is omitted from the API payload

// @TODO Remove this code once we've removed the ability to set UMAP Names in the API (CBS-695)
export function generateModelName(): string {
  // Generate a UUID
  const buffer = Buffer.alloc(16) // UUIDs are 16 bytes
  uuidv4({}, buffer)

  // Encode the Buffer as base64 and remove any trailing '=' characters
  const base64Url = buffer
    .toString('base64')
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')

  const modelName = `cell_vis_umap_${base64Url}`

  return modelName
}

export const StartAnalysisDlg = (props: StartAnalysisDialogProps): JSX.Element => {
  const {
    handleClose,
    handleStartAnalysis,
    isOpen,
    runs,
    customRunData,
    defaultSessionId = 0,
    hideMorphometricsModel = false,
  } = props

  const theme = useTheme()

  const { createSessionFromRuns } = useSessionApi()
  const { updateSessionAndVersion } = useCellVisualizationUrlParams()
  const demoEnabled = useFlagCondition('demoEnabled')
  const [sessionId, setSessionId] = useState(defaultSessionId)
  const [isCreatingSession, setIsCreatingSession] = useState(false)
  const { showProgress, showError, clearNotification } = useNotificationSlice()
  const hasStartedCreation = useRef(false)

  // Only enable sampling if there's more than one run
  // If there's only one run, then uniform random sampling is always good enough
  const samplingEnabled = runs.length > 1
  const eventsManager = useEventsManager()

  const triggerPreFilter = !demoEnabled
  const POLL_WAIT = 2000

  useQuery(['getSession', sessionId], () => getSession(sessionId, 1), {
    enabled: !!sessionId,
    retry: false,
    refetchInterval: POLL_WAIT,
    onError: async (error: Error) => {
      setSessionId(0)
      setIsCreatingSession(false)
      showError(error.message)
    },
    onSuccess: async (response) => {
      showProgress('Loading...')
      if (response?.data?.data?.status === 'ready') {
        setIsCreatingSession(false)
        updateSessionAndVersion(sessionId, 1, triggerPreFilter)
        clearNotification()
        if (handleStartAnalysis) {
          handleStartAnalysis()
        }
      }
    },
  })

  const { cellVisualizationsDefaultHFMModelId } = useFlags()

  const runFieldNames = customRunData ? _.keys(customRunData[0]) : undefined
  const [selectedRunFieldNames, setSelectedRunFieldNames] = useState<string[] | undefined>(
    runFieldNames
  )

  const [cancellationSource, setCancellationSource] = useState<CancelTokenSource | undefined>(
    undefined
  )

  const totalCellCount = totalCellCountInRuns(runs)
  const sampleSizeOptions = useMemo(
    () => getAvailableSampleSizeOptions(totalCellCount),
    [totalCellCount]
  )

  const [sampleSize, setSampleSize] = useState<number>(DEFAULT_SAMPLE_SIZE)
  const [morphoModel, setMorphoModel] = useState(cellVisualizationsDefaultHFMModelId)
  const [sessionName, setSessionName] = useState('')

  // By default, for now we sample uniformly from all cells
  //
  // This is the simplest behaviour, and also compatible with Axon releases before 1.0.8
  // And unless there's significant imbalance between runs or samples, the user may not want to
  // bother with the other options (which might have the side effect of reducing the amount of data
  // in the session if you don't have enough cells)
  const [samplingStrategy, setSamplingStrategy] = useState(SamplingStrategy.PROPORTIONAL)

  // What is browsable is the number of cell records uploaded, which can trail the
  // Controller-reported total_cell_count while protos are still landing, or never catch up if
  // some never arrived (SWT-1616). Fetched only for the selected runs and only once the user
  // picks custom sampling, because a cache miss costs a COUNT(*) over the cells table.
  const selectedRunIds = useMemo(() => runs.map((r) => r.run_id), [runs])
  const { data: cellRecordCounts } = useQuery(
    ['cellRecordCounts', selectedRunIds],
    () => getCellRecordCounts(selectedRunIds),
    {
      enabled: samplingStrategy === SamplingStrategy.CUSTOM && selectedRunIds.length > 0,
      staleTime: 5 * 60 * 1000,
    }
  )
  const [customSamplingConfig, setCustomSamplingConfig] = useState<RunSamplingConfig[]>([])

  // Add state for advanced options
  const [leidenResolution, setLeidenResolution] = useState<string>('0.5')
  const [nNeighbors, setNNeighbors] = useState<string>('15')
  const [minDist, setMinDist] = useState<string>('0.1')
  const [spread, setSpread] = useState<string>('1')
  const [advancedOptionsExpanded, setAdvancedOptionsExpanded] = useState<boolean>(false)

  const [projectionType, setProjectionType] = useState<'UMAP' | 'tSNE'>('UMAP')
  // '' is the sentinel value meaning "Default" — projection_model_id will be omitted from the payload
  // Source session for reprojection — null means "train a new model"
  const [sourceSession, setSourceSession] = useState<SessionMetaData | null>(null)
  const [sessionSearchText, setSessionSearchText] = useState<string>('')

  const projectionModelId =
    sourceSession?.projection_model_id ?? sourceSession?.create_spec?.projection_model_id ?? null

  const isReprojecting = sourceSession !== null

  // Fetch sessions that have a trained projection model, using keywords for partial matching
  const { data: sourceSessionsResponse, isLoading: isLoadingSourceSessions } = useQuery(
    ['getSourceSessions', sessionSearchText],
    () =>
      getSessions({
        has_projection_model: true,
        keywords: sessionSearchText ? sessionSearchText.trim().split(/\s+/) : undefined,
        limit: 20,
        order_by: 'updated_at:desc',
      }),
    { enabled: isOpen, staleTime: 30_000, keepPreviousData: true }
  )

  const sourceSessionItems = useMemo(() => {
    const sessions = sourceSessionsResponse?.data?.data ?? []
    return sessions
  }, [sourceSessionsResponse])

  // Debounce session name search to avoid a request on every keystroke
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSessionSearchChange = useCallback(
    _.debounce((value: string) => setSessionSearchText(value), 300),
    []
  )

  const [perplexity, setPerplexity] = useState('30')
  const [earlyExaggeration, setEarlyExaggeration] = useState('12')
  const [perplexityError, setPerplexityError] = useState<string>('')
  const [exaggerationError, setExaggerationError] = useState<string>('')

  const { cellVisualizationCreationTimeCoefficients } = useFlags()
  const timeEstimate = cellVisualizationCreationTimeCoefficients
    ? estimateCreationTime(totalCellCount, sampleSize, cellVisualizationCreationTimeCoefficients)
    : ''

  // Validate custom sampling config for API
  const isCustomSamplingValid = useMemo(() => {
    if (samplingStrategy !== SamplingStrategy.CUSTOM) {
      return true
    }
    if (customSamplingConfig.length === 0) {
      return false
    }

    const totalPercentage = customSamplingConfig.reduce((sum, c) => sum + (c.percentage || 0), 0)

    // Validation checks
    if (totalPercentage === 0) {
      return false
    }

    // Block if total exceeds 100%
    if (totalPercentage > 100) {
      return false
    }

    // Require total percentage to be at least 95% (95-99% shows warning, <95% blocks)
    if (totalPercentage < 95) {
      return false
    }

    return true
  }, [samplingStrategy, customSamplingConfig])

  // Prepare custom sampling config for API (send percentages, backend handles clamping)
  // Format: { "run_id": percentage, ... }
  const customSamplingForApi = useMemo(() => {
    if (samplingStrategy !== SamplingStrategy.CUSTOM) {
      return {}
    }
    return Object.fromEntries(
      customSamplingConfig.map((config) => [config.run_id, config.percentage || 0])
    )
  }, [samplingStrategy, customSamplingConfig])

  const tsneEnabled = useFlagCondition('cellVisualizationsEnableTSNE')

  const handleProjectionTypeChange = (event: SelectChangeEvent<unknown>) => {
    const newType = event.target.value as 'UMAP' | 'tSNE'
    setProjectionType(newType)
    if (newType === 'tSNE') {
      setSourceSession(null)
    }
    eventsManager.sendProjectionTypeSelected(newType)
  }
  const handleLeidenResolutionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLeidenResolution(e.target.value)
    eventsManager.sendProjectionParamChanged(projectionType, 'leiden_resolution', e.target.value)
  }
  const handleNNeighborsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNNeighbors(e.target.value)
    eventsManager.sendProjectionParamChanged(projectionType, 'num_neighbors', e.target.value)
  }
  const handleMinDistChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMinDist(e.target.value)
    eventsManager.sendProjectionParamChanged(projectionType, 'min_dist', e.target.value)
  }
  const handleSpreadChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSpread(e.target.value)
    eventsManager.sendProjectionParamChanged(projectionType, 'spread', e.target.value)
  }
  const handlePerplexityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setPerplexity(value)
    const num = parseFloat(value)
    if (value !== '' && (Number.isNaN(num) || num <= 0)) {
      setPerplexityError('Perplexity must be a positive number greater than 0.')
    } else {
      setPerplexityError('')
    }
    eventsManager.sendProjectionParamChanged(projectionType, 'perplexity', value)
  }
  const handleEarlyExaggerationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setEarlyExaggeration(value)
    const num = parseFloat(value)
    if (value !== '' && (Number.isNaN(num) || num <= 0)) {
      setExaggerationError('Early Exaggeration must be a positive number greater than 0.')
    } else if (value !== '' && num > 32) {
      setExaggerationError('Early Exaggeration cannot exceed 32.')
    } else {
      setExaggerationError('')
    }
    eventsManager.sendProjectionParamChanged(projectionType, 'early_exaggeration', value)
  }

  /* Each time the runs list changes, change the default option */
  useEffect(() => {
    const defaultOption = getDefaultOption(sampleSizeOptions)
    setSampleSize(defaultOption.value as number)
  }, [runs, sampleSizeOptions])

  // Note: tSNE is now available to all users when the flag is enabled

  // Reset all form fields when dialog closes
  useEffect(() => {
    // Only reset when dialog closes (isOpen changes from true to false)
    if (!isOpen) {
      // Reset form fields to default values
      setSessionName('')
      setSelectedRunFieldNames(runFieldNames)
      setSamplingStrategy(SamplingStrategy.PROPORTIONAL)
      setCustomSamplingConfig([])
      setMorphoModel(cellVisualizationsDefaultHFMModelId)
      setProjectionType('UMAP')
      setSourceSession(null)
      setSessionSearchText('')
      setPerplexity('30')
      setEarlyExaggeration('12')

      // Reset error states for tSNE params
      setPerplexityError('')
      setExaggerationError('')

      // Reset sample size to default
      const defaultOption = getDefaultOption(sampleSizeOptions)
      setSampleSize(defaultOption.value as number)

      // Reset advanced options
      setLeidenResolution('0.5')
      setNNeighbors('15')
      setMinDist('0.1')
      setSpread('1')
      setAdvancedOptionsExpanded(false)

      // Reset session creation state
      setIsCreatingSession(false)
    }
  }, [isOpen, cellVisualizationsDefaultHFMModelId, runFieldNames, sampleSizeOptions])

  // Reset isCreatingSession when any parameter changes, allowing parallel session creation
  useEffect(() => {
    // Only reset if we've started creation (button was clicked at least once)
    // This allows the user to create parallel sessions by changing parameters
    if (hasStartedCreation.current) {
      setIsCreatingSession(false)
      hasStartedCreation.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sessionName,
    projectionType,
    sourceSession,
    sampleSize,
    morphoModel,
    samplingStrategy,
    customSamplingConfig,
    leidenResolution,
    nNeighbors,
    minDist,
    spread,
    perplexity,
    earlyExaggeration,
    selectedRunFieldNames,
  ])

  const onStartAnalysis = async () => {
    setIsCreatingSession(true)
    hasStartedCreation.current = true

    // pick from fields based on fieldNames
    const selectedFields = selectedRunFieldNames
      ? customRunData?.map((field) =>
          selectedRunFieldNames.reduce((acc, fieldName) => {
            const name = fieldName === 'Run Id' ? 'run_id' : fieldName
            return { ...acc, [name]: field[fieldName] }
          }, {} as Record<string, unknown>)
        )
      : customRunData

    const projectionModelPayload = projectionModelId ? { projection_model_id: projectionModelId } : {}

    const createCancellationSource = axios.CancelToken.source()
    setCancellationSource(createCancellationSource)
    const config: VisualizationSessionConfig = {
      ...projectionModelPayload,
      runs: selectedFields ?? runs.map(({ run_id }) => ({ run_id })),
      morphometric_model: morphoModel,
      dl_only_projection: true,
      max_cell_count: sampleSize,
      cancelToken: createCancellationSource.token,
      name: sessionName,
      sampling_strategy: samplingStrategy,
      ...(samplingStrategy === SamplingStrategy.CUSTOM && Object.keys(customSamplingForApi).length > 0
        ? { percent_by_run_config: customSamplingForApi }
        : {}),
      leiden_resolution: parseFloat(leidenResolution),

      projection_params:
        projectionType === 'UMAP'
          ? {
              num_neighbors: parseInt(nNeighbors, 10),
              min_dist: parseFloat(minDist),
              spread: parseFloat(spread),
              method: projectionType,
            }
          : {
              perplexity: parseInt(perplexity, 10),
              exaggeration: parseFloat(earlyExaggeration),
              method: projectionType,
            },
    }
    eventsManager.sendCreateSesssionRequest(config)

    try {
      const result = await createSessionFromRuns(config)
      if (result?.data) {
        setSessionId(Number(result?.data.session_id))
      }
    } catch (error) {
      setIsCreatingSession(false)
      // Re-throw the error so it can be handled by the caller if needed
      throw error
    }
  }

  return (
    <DeepcellDialog
      handleCancel={(reason) => {
        if (handleClose && reason !== 'backdropClick') handleClose()
        if (cancellationSource) cancellationSource.cancel('Cancelled by user')
      }}
      handleConfirm={onStartAnalysis}
      open={isOpen}
      okLabel="Start Analysis"
      okDisabled={!morphoModel || !sessionName || isCreatingSession || !isCustomSamplingValid}
      titleLabel="Start Analysis"
    >
      <Stack spacing={1.5} sx={{ width: 550, px: 1 }}>
        <Box alignSelf="center" component="p" sx={{ color: theme.palette.info.dark, m: 0 }}>
          {`${runs.length} Run${runs.length === 1 ? '' : 's'} selected`}
        </Box>
        <DeepcellTextField
          label="Session Name"
          placeholder="Session Name"
          value={sessionName}
          onChange={(e) => setSessionName(e.target.value)}
          onFocus={(e) => e.target.select()}
        />
        {customRunData?.length ? (
          <FieldsToInclude
            options={runFieldNames}
            values={selectedRunFieldNames}
            onChange={setSelectedRunFieldNames}
          />
        ) : null}
        {projectionType !== 'tSNE' && (
          <Autocomplete<SessionMetaData>
            options={sourceSessionItems}
            filterOptions={(x) => x}
            value={sourceSession}
            onChange={(_e, value) => {
              setSourceSession(value)
            }}
            onInputChange={(_e, value) => handleSessionSearchChange(value)}
            getOptionLabel={(s) => s.name ?? `Session ${s.session_id}`}
            isOptionEqualToValue={(a, b) => a.session_id === b.session_id}
            loading={isLoadingSourceSessions}
            noOptionsText={sessionSearchText ? 'No matching sessions' : 'Type to search sessions'}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Project onto existing session (optional)"
                placeholder="Search by session name…"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingSourceSessions && <CircularProgress size={16} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        )}
        {isReprojecting && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
            Cells will be projected onto the embedding space of the selected session. UMAP
            parameters are fixed by the source session.
          </Typography>
        )}
        <DeepcellPrimarySelect
          label="Projection Type"
          items={[
            { value: 'UMAP', output: 'UMAP' },
            ...(tsneEnabled && !isReprojecting ? [{ value: 'tSNE', output: 'tSNE' }] : []),
          ]}
          value={projectionType}
          onChange={handleProjectionTypeChange}
          disabled={isReprojecting}
        />


        <DeepcellPrimarySelect
          label={`Number of Cells in ${projectionType}`}
          items={sampleSizeOptions.map((option) => ({
            value: option.value,
            output: option.output,
          }))}
          value={sampleSize}
          onChange={(e) => setSampleSize(Number(e.target.value))}
        />

        {projectionType === 'tSNE' && sampleSize > 10000 && (
          <Typography color="error" sx={{ fontSize: '0.95em', mt: 0.5 }}>
            tSNE calculations are much slower than UMAP for large datasets. We recommend using
            10,000 cells or fewer for initial analysis to avoid long processing times.
          </Typography>
        )}
        {!hideMorphometricsModel ? (
          <DeepcellPrimarySelect
            items={['hfm_d6:v1', 'hfm_d6_finetuned_morph_v9:v1']}
            value={morphoModel}
            onChange={(e) => setMorphoModel(`${e.target.value}`)}
            label="Morphometrics Model"
          />
        ) : null}
        {samplingEnabled && (
          <Stack>
            <FormControl>
              <FormLabel>Cell Sampling Method</FormLabel>
              <RadioGroup
                row
                value={samplingStrategy}
                onChange={(e) => setSamplingStrategy(e.target.value as SamplingStrategy)}
              >
                <DeepcellFormControlLabel
                  value={SamplingStrategy.PROPORTIONAL}
                  control={<Radio />}
                  label={getSamplingStrategyLabel(SamplingStrategy.PROPORTIONAL)}
                  tooltip="Sample cells randomly from all runs, with larger runs contributing proportionally more cells"
                />
                <DeepcellFormControlLabel
                  value={SamplingStrategy.EQUAL_BY_RUN}
                  control={<Radio />}
                  label={getSamplingStrategyLabel(SamplingStrategy.EQUAL_BY_RUN)}
                  tooltip="Sample an equal number of cells from each run. If we can't reach the target '# of Cells in UMAP,' we will use an equal number of cells from each run while trying to use as many cells as possible."
                />
                <DeepcellFormControlLabel
                  value={SamplingStrategy.EQUAL_BY_SAMPLE}
                  control={<Radio />}
                  label={getSamplingStrategyLabel(SamplingStrategy.EQUAL_BY_SAMPLE)}
                  tooltip="Sample an equal number of cells from each Sample ID. If we can't reach the target '# of Cells in UMAP,' we will use an equal number of cells from each Sample ID, while trying to use as many cells as possible."
                />
                <DeepcellFormControlLabel
                  value={SamplingStrategy.CUSTOM}
                  control={<Radio />}
                  label={getSamplingStrategyLabel(SamplingStrategy.CUSTOM)}
                  tooltip="Manually specify the number or percentage of cells to sample from each run. Useful when you want to include all cells from rare subpopulations while sampling proportionally from larger runs."
                />
              </RadioGroup>
            </FormControl>
          </Stack>
        )}
        {samplingEnabled && samplingStrategy === SamplingStrategy.CUSTOM && (
          <CustomSamplingConfig
            runs={runs}
            maxCellCount={sampleSize}
            samplingConfig={customSamplingConfig}
            onConfigChange={setCustomSamplingConfig}
            cellRecordCounts={cellRecordCounts}
          />
        )}

        {/* Advanced Options Section - hidden when reprojecting (params are fixed by source session) */}
        <Accordion
          expanded={isReprojecting ? false : advancedOptionsExpanded}
          onChange={() => !isReprojecting && setAdvancedOptionsExpanded(!advancedOptionsExpanded)}
          disabled={isReprojecting}
          sx={{ boxShadow: 'none', '&:before': { display: 'none' }, mt: 0 }}
        >
            <AccordionSummary
              expandIcon={
                advancedOptionsExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />
              }
              sx={{
                padding: '0px',
                minHeight: '36px',
                flexDirection: 'row-reverse',
                '& .MuiAccordionSummary-expandIconWrapper': {
                  marginRight: 1,
                },
                '& .MuiAccordionSummary-content': {
                  marginLeft: 0,
                },
              }}
            >
              <Typography sx={{ color: theme.palette.primary.main, fontWeight: 500 }}>
                Advanced Options
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: '0px 0px', pl: 4 }}>
              <Stack spacing={1} sx={{ mt: -0.5 }}>
                {/* Leiden Resolution with tooltip */}
                <DeepcellTextField
                  label="Leiden Clustering Resolution"
                  tooltip="Lower values create fewer clusters, and higher values create more clusters (default: 0.5)"
                  type="number"
                  value={leidenResolution}
                  onChange={handleLeidenResolutionChange}
                  inputProps={{ min: 0 }}
                  size="small"
                  margin="dense"
                  onFocus={(e) => e.target.select()}
                />

                {projectionType === 'UMAP' ? (
                  <>
                    {/* UMAP Parameters */}
                    <Box sx={{ mt: 0 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 500, fontSize: '0.875rem', mb: 0.25 }}
                      >
                        UMAP Parameters
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
                        {/* Neighbors parameter */}
                        <DeepcellTextField
                          label="# of Neighbors"
                          tooltip="Use a lower number to focus more on local structure, and higher number to focus more on global structure (default: 15)"
                          type="number"
                          value={nNeighbors}
                          onChange={handleNNeighborsChange}
                          inputProps={{ min: 1 }}
                          size="small"
                          margin="dense"
                          sx={{ flex: 1 }}
                          onFocus={(e) => e.target.select()}
                        />

                        {/* Min Dist parameter */}
                        <DeepcellTextField
                          label="Minimum Distance"
                          tooltip="Minimum distance between points in the layout. Smaller values will show finer structures and clustering, and bigger values will preserve more broad structure (default: 0.1)"
                          type="number"
                          value={minDist}
                          onChange={handleMinDistChange}
                          inputProps={{ min: 0, step: 0.1 }}
                          size="small"
                          margin="dense"
                          sx={{ flex: 1 }}
                          onFocus={(e) => e.target.select()}
                        />

                        {/* Spread parameter */}
                        <DeepcellTextField
                          label="Spread"
                          tooltip="Increase spread to increase global scaling (default: 1)"
                          type="number"
                          value={spread}
                          onChange={handleSpreadChange}
                          inputProps={{ min: 0, step: 0.5 }}
                          size="small"
                          margin="dense"
                          sx={{ flex: 1 }}
                          onFocus={(e) => e.target.select()}
                        />
                      </Box>
                    </Box>
                  </>
                ) : (
                  <>
                    {/* tSNE Parameters */}
                    <Box sx={{ mt: 0 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 500, fontSize: '0.875rem', mb: 0.25 }}
                      >
                        tSNE Parameters
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
                        {/* Perplexity parameter */}
                        <Box sx={{ flex: 1 }}>
                          <DeepcellTextField
                            label="Perplexity"
                            tooltip="Higher values will result in more global structure, and lower values will result in more local structure (default: 30)"
                            type="number"
                            value={perplexity}
                            onChange={handlePerplexityChange}
                            inputProps={{ min: 1 }}
                            size="small"
                            margin="dense"
                            onFocus={(e) => e.target.select()}
                          />
                          {perplexityError && (
                            <Typography color="error" sx={{ fontSize: '0.95em', mt: 0.5 }}>
                              {perplexityError}
                            </Typography>
                          )}
                        </Box>

                        {/* Early Exaggeration parameter */}
                        <Box sx={{ flex: 1 }}>
                          <DeepcellTextField
                            label="Early Exaggeration"
                            tooltip="Controls how tight natural clusters in the original space are in the embedded space (default: 12, max: 32)"
                            type="number"
                            value={earlyExaggeration}
                            onChange={handleEarlyExaggerationChange}
                            inputProps={{ min: 1, max: 32 }}
                            size="small"
                            margin="dense"
                            onFocus={(e) => e.target.select()}
                          />
                          {exaggerationError && (
                            <Typography color="error" sx={{ fontSize: '0.95em', mt: 0.5 }}>
                              {exaggerationError}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>

        {timeEstimate ? (
          <Box alignSelf="left" component="p" sx={{ color: theme.palette.info.dark, my: 0.5 }}>
            Estimated time to start analysis: {timeEstimate}
          </Box>
        ) : null}
      </Stack>
    </DeepcellDialog>
  )
}

export default StartAnalysisDlg
